import { Component } from '@theme/component';

/**
 * A custom element that handles the header search input and modal integration.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} headerSearchInput - The header search input element.
 *
 * @extends {Component<Refs>}
 */
class HeaderSearchComponent extends Component {
  requiredRefs = ['headerSearchInput'];

  #controller = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;

    // Listen for Enter key on the header search input
    this.refs.headerSearchInput.addEventListener('keydown', this.#handleInputKeyDown, { signal });

    // Listen for search modal dialog open events
    const searchModal = document.querySelector('#search-modal');
    if (searchModal) {
      searchModal.addEventListener('dialog:open', this.#handleModalOpen, { signal });
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
  }

  /**
   * Handles keydown events on the header search input.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleInputKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      this.#openSearchModal();
    }
  };

  /**
   * Handles the modal open event.
   * This fires when the icon button is clicked OR when Enter is pressed.
   */
  #handleModalOpen = () => {
    this.#transferSearchValue();
  };

  /**
   * Opens the search modal.
   */
  #openSearchModal = () => {
    const searchModal = document.querySelector('#search-modal');
    if (!searchModal) return;

    // Open the modal (this will trigger the dialog-open event)
    searchModal.showDialog();
  };

  /**
   * Transfers the search value from header input to modal input and triggers search.
   */
  #transferSearchValue = () => {
    const searchValue = this.refs.headerSearchInput.value.trim();

    if (!searchValue) return; // Don't transfer if empty

    const searchModal = document.querySelector('#search-modal');

    if (!searchModal) return;

    // Wait for the modal to be fully rendered and DOM updated
    setTimeout(() => {
      const predictiveSearch = searchModal.querySelector('predictive-search-component');

      if (!predictiveSearch) {
        console.warn('Predictive search component not found in modal');
        return;
      }

      const modalInput = predictiveSearch.querySelector('input[type="search"]');

      if (!modalInput) {
        console.warn('Modal search input not found');
        return;
      }

      // Set the value in the modal's search input
      modalInput.value = searchValue;

      // Trigger the search by dispatching an input event
      const inputEvent = new InputEvent('input', {
        bubbles: true,
        cancelable: true,
        inputType: 'insertText',
      });

      modalInput.dispatchEvent(inputEvent);

      // Focus the modal input after a brief delay
      requestAnimationFrame(() => {
        modalInput.focus();
      });
    }, 100);
  };
}

customElements.define('search-button', HeaderSearchComponent);
